import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Code2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Pick-by-code dialog (feedback 22.05.2026).
 *
 * Lets sales/partner/admin hand-pick the EXACT FDP product codes (CP) the AI
 * should use for the render. The user selects up to MAX codes across one or
 * more pre-chosen categories. The result is a comma-separated string of
 * codes that the parent posts as `selected_product_codes` to the simulation
 * endpoint, which locks the AI prompt and the product_codes_used badges to
 * those exact CPs.
 *
 * Why a separate dialog instead of inline:
 *   1. Picker can have 50+ profiles per category — needs scroll + thumbnails
 *      that would crowd the main form.
 *   2. simple_user role never sees this — it's hidden behind a role-gated
 *      button in DashboardSimulate.
 */
const MAX_PICKS = 10;

export default function ProductCodePickerDialog({
  open,
  onOpenChange,
  simType, // "INTERIOR" | "EXTERIOR"
  style, // current style slug e.g. "ART_DECO"
  categories, // string[] of already-selected category slugs
  initial, // string[] of already-picked codes (for re-open)
  onConfirm, // (codes: string[]) => void
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set(initial || []));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(initial || []));
    if (!categories || categories.length === 0) {
      setData({ categories: [] });
      return;
    }
    setLoading(true);
    api
      .get("/catalog/product-codes", {
        params: {
          sim_type: simType,
          style,
          categories: categories.join(","),
        },
      })
      .then((r) => setData(r.data))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("product-codes fetch failed", e);
        toast.error(t("picker.toast_load_failed"));
        setData({ categories: [] });
      })
      .finally(() => setLoading(false));
    // intentionally not depending on `initial` — that's only for the initial seed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, simType, style, categories]);

  const totalAvailable = useMemo(
    () => (data?.categories || []).reduce((acc, c) => acc + c.codes.length, 0),
    [data],
  );

  const toggleCode = (code) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else if (next.size >= MAX_PICKS) {
        toast.warning(t("picker.toast_max", { max: MAX_PICKS }));
        return prev;
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(picked));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-white max-w-4xl max-h-[88vh] overflow-y-auto"
        data-testid="product-code-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Code2 size={18} className="text-orange-400" />
            {t("picker.title")}
          </DialogTitle>
          <p className="text-zinc-400 text-sm mt-1">
            {t("picker.desc")}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400 py-8">
            <Loader2 className="animate-spin" size={16} /> {t("picker.loading")}
          </div>
        ) : (data?.categories || []).length === 0 ? (
          <div className="text-zinc-400 text-sm py-8 text-center" data-testid="product-codes-empty">
            {t("picker.empty")}
          </div>
        ) : (
          <div className="space-y-5">
            {data.categories.map((cat) => (
              <div key={cat.slug} className="border border-white/10 rounded-lg p-3 bg-zinc-900/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-outfit font-bold text-white text-sm uppercase tracking-[0.2em]">
                    {cat.name}
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {cat.codes.length} {cat.codes.length === 1 ? t("picker.code_singular") : t("picker.code_plural")}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {cat.codes.map((c) => {
                    const isPicked = picked.has(c.code);
                    return (
                      <button
                        type="button"
                        key={c.code}
                        onClick={() => toggleCode(c.code)}
                        className={`relative text-left rounded-md border-2 overflow-hidden transition-all ${
                          isPicked
                            ? "border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/30"
                            : "border-white/10 hover:border-white/25 bg-zinc-900/40"
                        }`}
                        data-testid={`product-code-${c.code}`}
                      >
                        {c.url ? (
                          <img
                            src={fileUrl(c.url)}
                            alt={c.code}
                            className="w-full h-20 object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-20 bg-zinc-800 flex items-center justify-center text-zinc-600 text-xs">
                            {t("picker.no_thumb")}
                          </div>
                        )}
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="font-mono font-bold text-xs text-white">
                            {c.code}
                          </span>
                          {isPicked && (
                            <Check size={14} className="text-orange-400" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-2">
          <div className="text-xs text-zinc-400">
            <span className="text-white font-bold" data-testid="picked-count">
              {picked.size}
            </span>{" "}
            / {MAX_PICKS} {t("picker.picked_count_suffix")}{" "}
            <span className="text-zinc-600">· {totalAvailable} {t("picker.available_count_suffix")}</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPicked(new Set())}
              className="text-zinc-400 hover:text-white hover:bg-white/5"
              data-testid="picker-clear"
            >
              {t("picker.clear_btn")}
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="picker-confirm"
            >
              <Check size={14} className="mr-1.5" />
              {t("picker.confirm_btn", { n: picked.size })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
